import { useMsal } from '@azure/msal-react';
import {
    getAccessTokenExpiration,
    useHasPermission,
    usePermissionMap,
    usePermissionsMock,
    useUserDetails,
} from '../../../services/auth/auth';
import ReactTimeago from 'react-timeago';
import { Button } from '../../shared/inputs/button';
import React, { useState } from 'react';
import { ProfilePhoto } from '../../shared/profilePhoto/profilePhoto';
import { iconMap, knownEntities } from '../../../services/routing/routes';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import { ReactComponent as VisibleIcon } from '../../../assets/icons/visible.svg';
import { useNavigate } from 'react-router-dom';
import { Grid } from '../../shared/grid/grid';
import { useDevMode, useDevModeSetter } from '../../../services/devMode';
import { css } from '../../../services/css';
import { Notify } from '../../../services/notification/notify';
import { USER } from '../../../permissions';

import styles from './userPulldown.module.css';

export const UserPulldown = ({ setDown }) => {
    const { instance } = useMsal();
    const permissions = usePermissionMap();
    const details = useUserDetails();
    const navigate = useNavigate();
    const devMode = useDevMode();
    const devModeSetter = useDevModeSetter();

    const permissionsMock = usePermissionsMock();

    const canReadUser = useHasPermission(USER.READ);

    const signOut = () => {
        instance
            .logoutPopup()
            .then(() => {
                instance.loginRedirect();
            })
            .catch((e) => {
                console.error(e);
            });
    };

    const viewSelf = () => {
        setDown(false);
        navigate('/users/' + details?.localAccountId);
    };

    const getPermissionsMapArray = () =>
        Object.entries(
            devMode
                ? {
                      ...Object.fromEntries(knownEntities.map((e) => [e, []])),
                      ...permissions,
                  }
                : permissions
        );

    let timeout;

    const [clickCount, setClickCount] = useState(0);

    // Triple Click to toggle dev mode
    const handleProfileClick = () => {
        if (timeout) {
            clearTimeout(timeout);
            timeout = undefined;
        }

        if (clickCount + 1 >= 3) {
            setDevMode(!devMode);

            setClickCount(0);
            setDown(false);
        } else {
            setClickCount(clickCount + 1);

            timeout = setTimeout(() => {
                setClickCount(0);
            }, 500);
        }
    };

    const setDevMode = (state) => {
        devModeSetter(state);
        Notify.success(
            `Dev Mode ${state ? 'Enabled' : 'Disabled'}`,
            `You have just ${state ? 'enabled' : 'disabled'} dev mode. ` +
                {
                    true: 'To disable expand the user info and click the Disable Dev Mode button.',
                    false: 'To enable, expand the user info and triple click the profile photo.',
                }[state]
        );
        setDown(false);
    };

    return (
        <section className={styles.Info}>
            <div className={styles.Icon}>
                <ProfilePhoto
                    userId={details?.localAccountId}
                    hasProfilePhoto={true}
                    onClick={handleProfileClick}
                />
            </div>

            <div className={styles.Details}>
                <h1>{details?.name}</h1>

                <details className={styles.PermissionsDetails}>
                    <summary>
                        <h2>Permissions</h2>
                    </summary>

                    <div className={styles.Permissions}>
                        <div
                            className={
                                styles.Permission + ' ' + styles.Heading
                            }>
                            <div>Resource</div>
                            <div>Read</div>
                            <div>Edit</div>
                        </div>

                        {getPermissionsMapArray().map(
                            ([resource, permissions]) => {
                                let Icon = iconMap[resource];

                                if (!Icon)
                                    return (
                                        <React.Fragment
                                            key={resource}></React.Fragment>
                                    );

                                return (
                                    <div
                                        key={resource}
                                        className={styles.Permission}>
                                        <div
                                            className={css(
                                                styles.Icon,
                                                'icon'
                                            )}>
                                            <Icon />
                                        </div>
                                        <div className={styles.ResourceName}>
                                            {resource}
                                        </div>
                                        <div
                                            className={styles.Read}
                                            title='Read Permissions'>
                                            <Button
                                                className={css(
                                                    styles.Icon,
                                                    styles.IconBtn,
                                                    'icon',
                                                    !permissions.includes(
                                                        'Read'
                                                    ) && styles.Hidden
                                                )}
                                                onClick={() =>
                                                    devMode &&
                                                    permissionsMock.toggle(
                                                        resource + '.Read'
                                                    )
                                                }
                                                disabled={!devMode}
                                                title={resource + '.Read'}>
                                                <VisibleIcon />
                                            </Button>
                                        </div>
                                        <div
                                            className={styles.Edit}
                                            title='Edit Permissions'>
                                            <Button
                                                className={css(
                                                    styles.Icon,
                                                    styles.IconBtn,
                                                    'icon',
                                                    !permissions.includes(
                                                        'Write'
                                                    ) && styles.Hidden
                                                )}
                                                onClick={() =>
                                                    devMode &&
                                                    permissionsMock.toggle(
                                                        resource + '.Write'
                                                    )
                                                }
                                                disabled={!devMode}
                                                title={resource + '.Write'}>
                                                <EditIcon />
                                            </Button>
                                        </div>
                                    </div>
                                );
                            }
                        )}
                    </div>

                    {devMode && (
                        <div className={styles.PermissionsActions}>
                            <Button onClick={() => permissionsMock.reset()}>
                                Reset
                            </Button>
                            <Button
                                className='icon'
                                onClick={() =>
                                    permissionsMock.add(
                                        prompt('Permission to add')
                                    )
                                }>
                                +
                            </Button>
                            <Button
                                className='icon'
                                onClick={() =>
                                    permissionsMock.remove(
                                        prompt('Permission to remove')
                                    )
                                }>
                                -
                            </Button>
                        </div>
                    )}
                </details>

                {devMode && (
                    <details>
                        <summary>
                            <h2>Debug Info</h2>
                        </summary>

                        <Grid
                            columns={{
                                0: { label: 'Stat', sortable: false },
                                1: { label: 'Value', sortable: false },
                            }}
                            data={[
                                // @ts-ignore
                                [
                                    'Token Expiration',
                                    <ReactTimeago
                                        date={getAccessTokenExpiration()}
                                    />,
                                ],
                            ]}
                        />
                    </details>
                )}

                <div className={styles.Actions}>
                    {canReadUser && (
                        <Button
                            className='primary'
                            onClick={viewSelf}>
                            View Account
                        </Button>
                    )}
                    <Button
                        className={styles.Logout}
                        onClick={signOut}>
                        Log Out
                    </Button>
                    {devMode && (
                        <Button
                            className={styles.DisableDevMode}
                            onClick={() => setDevMode(false)}>
                            Disable Dev Mode
                        </Button>
                    )}
                </div>
            </div>
        </section>
    );
};
